import { defineStore } from "pinia";

import cargoItemRegistriesApi from "@/services/api/cargoItemRegistriesApi";

import { getCompanyId } from "@/utils/userUtils";

export const useCargoItemRegistryStore = defineStore(
  "cargoItemRegistryStore",
  {
    state: () => ({
      items: [],
      companyId: getCompanyId(),
      isLoading: false,
      error: null,
      lastFetchParams: {},

      pagination: {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
      },
    }),

    getters: {
      activeItems: (state) =>
        state.items.filter((i) => i.enabled !== false),
    },

    actions: {
      async getItemsRequest(params = {}) {
        this.isLoading = true;
        this.lastFetchParams = params;
        try {
          const response = await cargoItemRegistriesApi.index(
            this.companyId,
            params
          );
          this.items = response.data.result || response.data || [];

          const meta = response.data.pagination_meta;
          if (meta) {
            this.pagination = {
              page: meta.page,
              pages: meta.pages,
              series: meta.series,
              next: meta.next,
              prev: meta.prev,
              count: meta.count,
              limit: meta.limit,
            };
          }
        } catch (error) {
          this.error = error;
          console.error("Ошибка при получении позиций груза:", error);
        } finally {
          this.isLoading = false;
        }
      },

      async loadMoreItems() {
        if (
          this.isLoading ||
          this.pagination.page >= this.pagination.pages
        )
          return;

        this.isLoading = true;
        try {
          const response = await cargoItemRegistriesApi.index(
            this.companyId,
            {
              ...this.lastFetchParams,
              page: this.pagination.page + 1,
            }
          );

          this.items = [
            ...this.items,
            ...(response.data.result || response.data || []),
          ];

          const meta = response.data.pagination_meta;
          if (meta) {
            this.pagination = {
              page: meta.page,
              pages: meta.pages,
              series: meta.series,
              next: meta.next,
              prev: meta.prev,
              count: meta.count,
              limit: meta.limit,
            };
          }
        } catch (error) {
          this.error = error;
          console.error("Ошибка при подгрузке позиций груза:", error);
        } finally {
          this.isLoading = false;
        }
      },

      async showItem(id) {
        try {
          const response = await cargoItemRegistriesApi.show(
            this.companyId,
            id
          );

          return response.data.result || response.data || {};
        } catch (error) {
          this.error = error;
          console.error("Ошибка при получении позиции груза:", error);
        }
      },

      async createItem(payload) {
        this.isLoading = true;
        try {
          const response = await cargoItemRegistriesApi.create(
            this.companyId,
            payload
          );
          await this.getItemsRequest(this.lastFetchParams);

          return response.data.result || response.data || {};
        } catch (error) {
          console.error("Ошибка при создании позиции груза:", error);
          throw error;
        } finally {
          this.isLoading = false;
        }
      },

      async updateItem(id, payload) {
        this.isLoading = true;
        try {
          await cargoItemRegistriesApi.update(
            this.companyId,
            id,
            payload
          );

          await this.getItemsRequest(this.lastFetchParams);
        } catch (error) {
          console.error(
            "Ошибка при обновлении позиции груза:",
            error
          );
          throw error;
        } finally {
          this.isLoading = false;
        }
      },

      async itemEnable(id) {
        this.isLoading = true;
        try {
          await cargoItemRegistriesApi.enable(this.companyId, id);

          await this.getItemsRequest(this.lastFetchParams);
        } catch (error) {
          console.error("Ошибка при включении позиции груза:", error);
          throw error;
        } finally {
          this.isLoading = false;
        }
      },

      async itemDisable(id) {
        this.isLoading = true;
        try {
          await cargoItemRegistriesApi.disable(this.companyId, id);

          await this.getItemsRequest(this.lastFetchParams);
        } catch (error) {
          console.error(
            "Ошибка при отключении позиции груза:",
            error
          );
          throw error;
        } finally {
          this.isLoading = false;
        }
      },
    },
  }
);
